// Admin Notes — Steve's personal notes (markdown).
//
// Source-of-truth reference: /var/www/kitchenos/admin-standalone.html
//   page section: ~line 1441 (#page-notes — addNote / renderNotes)
//   data:         /var/www/kitchenos-api/data/admin_notes.json
//
// Backend (verified in /var/www/kitchenos-api/server.js, line ~1101):
//   GET    /api/admin/notes       — list
//   POST   /api/admin/notes       — { title, content } -> ServerNote
//   PATCH  /api/admin/notes/:id   — partial update (title, content, pinned, category)
//   DELETE /api/admin/notes/:id   — remove
//
// Server stores: { id, title, content, createdAt, updatedAt } and passes
// unknown fields through via `{ ...notes[idx], ...req.body }`. We add `pinned`
// and `category` client-side.
//
// Markdown rendering is intentionally tiny: bold / italic / inline code /
// headings / links / line breaks — enough for personal notes without pulling
// in a dependency.

import { ApiError } from "@tkos/api-client";

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

interface ServerNote {
  id: string;
  title: string;
  content: string;
  pinned?: boolean;
  category?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

interface Note {
  id: string;
  title: string;
  content: string;
  pinned: boolean;
  category: string;
  updatedAt: number;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function coerceTime(raw: unknown): number {
  if (typeof raw === "string" && raw.trim()) {
    const t = Date.parse(raw);
    if (Number.isFinite(t)) return t;
  }
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  return Date.now();
}

function normalise(n: ServerNote): Note {
  return {
    id: String(n.id ?? ""),
    title: String(n.title ?? "").trim() || "Untitled",
    content: String(n.content ?? ""),
    pinned: !!n.pinned,
    category:
      typeof n.category === "string" && n.category.trim() !== ""
        ? n.category.trim()
        : "",
    updatedAt: coerceTime(n.updatedAt ?? n.createdAt),
  };
}

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Tiny markdown subset — we escape first, then re-introduce safe tags. */
function renderMarkdown(src: string): string {
  let s = esc(src);
  // Headings (### / ## / #) at line start
  s = s.replace(/^### (.*)$/gm, "<h3>$1</h3>");
  s = s.replace(/^## (.*)$/gm, "<h2>$1</h2>");
  s = s.replace(/^# (.*)$/gm, "<h1>$1</h1>");
  // Bold **x**
  s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  // Italic *x*
  s = s.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  // Inline code `x`
  s = s.replace(/`([^`]+)`/g, "<code>$1</code>");
  // Links [label](url) — url already escaped via &quot;
  s = s.replace(
    /\[([^\]]+)\]\(((?:https?:\/\/|\/|#)[^\s)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
  );
  // Line breaks
  s = s.replace(/\n/g, "<br/>");
  return s;
}

const dateFmt = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

// ── Page ─────────────────────────────────────────────────────────────────────

const notesPage: Page = {
  name: "notes",
  label: "Notes",
  icon: "📝",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const ac = new AbortController();
    let notes: Note[] = [];
    let selectedId: string | null = null;
    let loading = true;
    let error: string | null = null;
    let saveTimer: number | null = null;
    let preview = false;

    const root = document.createElement("section");
    root.className = "anote-page";
    root.innerHTML = `
      <header class="anote-header">
        <h1>Admin Notes</h1>
        <p>Markdown-friendly personal notes. Auto-saves a second after you stop typing.</p>
      </header>

      <div class="anote-split">
        <aside class="anote-sidebar">
          <div class="anote-side-head">
            <button class="adm-btn" type="button" data-role="new">+ New note</button>
          </div>
          <div class="anote-list" data-role="list"></div>
        </aside>
        <section class="anote-editor" data-role="editor">
          <div class="adm-empty">Select or create a note</div>
        </section>
      </div>
    `;
    container.appendChild(root);

    const listEl = root.querySelector<HTMLDivElement>("[data-role='list']")!;
    const editorEl = root.querySelector<HTMLElement>("[data-role='editor']")!;
    const newBtn = root.querySelector<HTMLButtonElement>("[data-role='new']")!;

    function renderList(): void {
      if (loading) {
        listEl.innerHTML = `<div class="adm-loading">Loading…</div>`;
        return;
      }
      if (error) {
        listEl.innerHTML = `<div class="adm-error">${esc(error)}</div>`;
        return;
      }
      if (notes.length === 0) {
        listEl.innerHTML = `<div class="adm-empty">No notes yet</div>`;
        return;
      }
      const sorted = [...notes].sort((a, b) => {
        if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
        return b.updatedAt - a.updatedAt;
      });
      listEl.innerHTML = sorted
        .map(
          (n) => `
        <button class="anote-item${n.id === selectedId ? " anote-item-active" : ""}" type="button" data-id="${esc(n.id)}">
          <div class="anote-item-title">${n.pinned ? "📌 " : ""}${esc(n.title)}</div>
          <div class="anote-item-meta">
            ${n.category ? `<span class="adm-pill anote-cat">${esc(n.category)}</span>` : ""}
            <span class="anote-item-date">${dateFmt.format(n.updatedAt)}</span>
          </div>
        </button>`,
        )
        .join("");
    }

    function renderEditor(): void {
      const note = notes.find((n) => n.id === selectedId);
      if (!note) {
        editorEl.innerHTML = `<div class="adm-empty">Select or create a note</div>`;
        return;
      }
      editorEl.innerHTML = `
        <div class="anote-toolbar">
          <input class="adm-input anote-title-input" data-role="title" value="${esc(note.title)}" placeholder="Title" />
          <input class="adm-input anote-cat-input" data-role="category" value="${esc(note.category)}" placeholder="Category" />
          <label class="anote-pin">
            <input type="checkbox" data-role="pinned" ${note.pinned ? "checked" : ""} /> Pin
          </label>
          <button class="adm-btn adm-btn-ghost" type="button" data-role="toggle-preview">${preview ? "Edit" : "Preview"}</button>
          <button class="adm-btn adm-btn-danger" type="button" data-role="delete">Delete</button>
        </div>
        ${
          preview
            ? `<div class="anote-preview">${renderMarkdown(note.content)}</div>`
            : `<textarea class="adm-textarea anote-content" data-role="content" rows="20" placeholder="Write in markdown…">${esc(note.content)}</textarea>`
        }
        <div class="anote-status" data-role="status"></div>
      `;
    }

    function setStatus(msg: string): void {
      const s = editorEl.querySelector<HTMLElement>("[data-role='status']");
      if (s) s.textContent = msg;
    }

    async function load(): Promise<void> {
      loading = true;
      error = null;
      renderList();
      try {
        const raw = await ctx.api<ServerNote[]>("/admin/notes", { signal: ac.signal });
        notes = (Array.isArray(raw) ? raw : []).map(normalise);
        loading = false;
        if (!selectedId && notes.length > 0) selectedId = notes[0]!.id;
        renderList();
        renderEditor();
      } catch (err) {
        if (isAbortError(err)) return;
        loading = false;
        error = err instanceof ApiError ? `Couldn't load notes (${err.status}).` : "Couldn't load notes.";
        renderList();
      }
    }

    async function createNote(): Promise<void> {
      try {
        const created = await ctx.api<ServerNote>("/admin/notes", {
          method: "POST",
          body: JSON.stringify({ title: "New note", content: "" }),
          signal: ac.signal,
        });
        const n = normalise(created);
        notes = [n, ...notes];
        selectedId = n.id;
        preview = false;
        renderList();
        renderEditor();
      } catch (err) {
        if (isAbortError(err)) return;
        ctx.showToast("Couldn't create note", "error");
      }
    }

    async function patch(id: string, body: Partial<ServerNote>): Promise<void> {
      try {
        const updated = await ctx.api<ServerNote>(`/admin/notes/${encodeURIComponent(id)}`, {
          method: "PATCH",
          body: JSON.stringify(body),
          signal: ac.signal,
        });
        const n = normalise(updated);
        notes = notes.map((x) => (x.id === id ? n : x));
        renderList();
        setStatus("Saved");
        window.setTimeout(() => setStatus(""), 1200);
      } catch (err) {
        if (isAbortError(err)) return;
        setStatus("Save failed");
      }
    }

    async function deleteNote(id: string): Promise<void> {
      if (!window.confirm("Delete this note?")) return;
      try {
        await ctx.api<{ ok: true }>(`/admin/notes/${encodeURIComponent(id)}`, {
          method: "DELETE",
          signal: ac.signal,
        });
        notes = notes.filter((n) => n.id !== id);
        if (selectedId === id) selectedId = notes[0]?.id ?? null;
        renderList();
        renderEditor();
      } catch (err) {
        if (isAbortError(err)) return;
        ctx.showToast("Couldn't delete note", "error");
      }
    }

    function scheduleSave(id: string, patchBody: Partial<ServerNote>): void {
      if (saveTimer != null) window.clearTimeout(saveTimer);
      setStatus("Saving…");
      saveTimer = window.setTimeout(() => {
        saveTimer = null;
        void patch(id, patchBody);
      }, 700);
    }

    const onListClick = (e: Event): void => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>(".anote-item");
      if (!btn) return;
      const id = btn.dataset.id ?? null;
      if (id && id !== selectedId) {
        selectedId = id;
        preview = false;
        renderList();
        renderEditor();
      }
    };

    const onEditorInput = (e: Event): void => {
      const id = selectedId;
      if (!id) return;
      const t = e.target as HTMLElement;
      const note = notes.find((n) => n.id === id);
      if (!note) return;
      if (t.matches("[data-role='title']")) {
        const v = (t as HTMLInputElement).value;
        note.title = v.trim() || "Untitled";
        scheduleSave(id, { title: v });
      } else if (t.matches("[data-role='content']")) {
        note.content = (t as HTMLTextAreaElement).value;
        scheduleSave(id, { content: note.content });
      } else if (t.matches("[data-role='category']")) {
        note.category = (t as HTMLInputElement).value.trim();
        scheduleSave(id, { category: note.category });
      }
    };

    const onEditorChange = (e: Event): void => {
      const id = selectedId;
      if (!id) return;
      const t = e.target as HTMLElement;
      if (t.matches("[data-role='pinned']")) {
        const note = notes.find((n) => n.id === id);
        if (!note) return;
        note.pinned = (t as HTMLInputElement).checked;
        renderList();
        void patch(id, { pinned: note.pinned });
      }
    };

    const onEditorClick = (e: Event): void => {
      const t = e.target as HTMLElement;
      const id = selectedId;
      if (t.matches("[data-role='toggle-preview']")) {
        preview = !preview;
        renderEditor();
      } else if (t.matches("[data-role='delete']") && id) {
        void deleteNote(id);
      }
    };

    listEl.addEventListener("click", onListClick);
    editorEl.addEventListener("input", onEditorInput);
    editorEl.addEventListener("change", onEditorChange);
    editorEl.addEventListener("click", onEditorClick);
    newBtn.addEventListener("click", () => void createNote());

    void load();

    return () => {
      ac.abort();
      if (saveTimer != null) window.clearTimeout(saveTimer);
      listEl.removeEventListener("click", onListClick);
      editorEl.removeEventListener("input", onEditorInput);
      editorEl.removeEventListener("change", onEditorChange);
      editorEl.removeEventListener("click", onEditorClick);
      root.remove();
    };
  },
};

registerPage(notesPage);
export default notesPage;
